import { api } from './api'

export const clientsService = {
  /**
   * Lista de clientes (alcance por tenant + headers).
   * @param {string|number} companyId — reservado por compatibilidad; el API usa empresa activa en headers
   * @param {{ scope?: 'all' | 'mine', tenantId?: string }} [options] — `mine` = solo clientes del worker
   * (no admin); `tenantId` = empresa distinta a la activa (ver api.js get()).
   */
  async getAll(companyId, options = {}) {
    try {
      const params = new URLSearchParams()
      if (options.scope === 'mine') {
        params.set('scope', 'mine')
      }
      // `q` filtra en el backend sobre toda la tabla; sin él solo se busca en la primera página.
      const q = String(options.q ?? '').trim()
      if (q) params.set('q', q)
      if (options.limit != null) params.set('limit', String(options.limit))
      if (options.page != null) params.set('page', String(options.page))
      const qs = params.toString()
      const path = qs ? `/clients?${qs}` : '/clients'
      const response = await api.get(path, true, options.tenantId ? { tenantId: options.tenantId } : {})
      return response
    } catch (error) {
      console.error('Error obteniendo clientes:', error)
      throw error
    }
  },

  /*Obtiene un cliente por su ID*/
  async getById(clientId) {
    try {
      const response = await api.get(`/clients/${clientId}`, true)
      return response
    } catch (error) {
      console.error('Error obteniendo cliente:', error)
      throw error
    }
  },

  /** Saved references of a client (defaults for appointments). Returns [{clientId,name,phone}]. */
  async getReferences(clientId) {
    try {
      const response = await api.get(`/clients/${clientId}/references`, true)
      return response?.data ?? []
    } catch (error) {
      console.error('Error obteniendo referencias del cliente:', error)
      throw error
    }
  },

  /** Replace a client's saved references. `references` = [{clientId?, name?, phone?}]. */
  async saveReferences(clientId, references) {
    try {
      const payload = {
        references: (references || [])
          .map((r) => ({
            client_id: r.clientId ?? r.client_id ?? undefined,
            full_name: String(r.name ?? r.full_name ?? '').trim() || undefined,
            phone: String(r.phone ?? '').trim() || undefined,
          }))
          .filter((r) => r.client_id || r.full_name || r.phone),
      }
      const response = await api.put(`/clients/${clientId}/references`, payload, true)
      return response?.data ?? []
    } catch (error) {
      console.error('Error guardando referencias del cliente:', error)
      throw error
    }
  },

  /**
   * Expediente del cliente (notas, metadatos) y lista `documents` para el gestor de archivos.
   * GET /clients/:clientId/record — requiere x-company-tenant-id.
   * @param {number|string} clientId
   */
  async getClientRecord(clientId) {
    try {
      const response = await api.get(`/clients/${clientId}/record`, true)
      return response
    } catch (error) {
      console.error('Error obteniendo expediente del cliente:', error)
      throw error
    }
  },

  /**Crea un nuevo cliente. tenantId: empresa distinta a la activa (ver api.js post()).*/
  async create(companyId, clientData, tenantId) {
    try {
      // Validar que los datos existan
      if (!clientData.clientName || clientData.clientName.trim().length < 2) {
        throw new Error('El nombre debe tener al menos 2 caracteres')
      }

      if (!clientData.phone || clientData.phone.trim().length < 10) {
        throw new Error('El teléfono debe tener al menos 10 caracteres')
      }

      const payload = {
        full_name: clientData.clientName.trim(),
        phone: clientData.phone.trim(),
        notes: clientData.comments ? clientData.comments.trim() : '',
        company_id: companyId,
        company_notes: clientData.comments ? clientData.comments.trim() : ''
      }

      // Si hay email, lo agregamos
      if (clientData.email && clientData.email.trim()) {
        payload.email = clientData.email.trim()
      }
      const response = await api.post('/clients', payload, true, tenantId ? { tenantId } : {})
      return response
    } catch (error) {
      console.error('Error creando cliente:', error)
      console.error('Detalles del error:', error.response?.data)
      console.error('Mensaje de error:', error.message)
      throw error
    }
  },

  /*Actualiza un cliente existente. tenantId: empresa distinta a la activa (ver api.js put()).*/
  async update(clientId, clientData, companyId, tenantId) {
    try {
      const payload = { company_id: companyId }
      const has = (key) => Object.prototype.hasOwnProperty.call(clientData, key)

      if (has('full_name') || has('clientName')) {
        const name = clientData.full_name ?? clientData.clientName ?? ''
        payload.full_name = String(name).trim()
      }

      if (has('phone') && clientData.phone != null) {
        payload.phone = String(clientData.phone).trim()
      }

      if (has('email')) {
        payload.email = clientData.email != null ? String(clientData.email).trim() : ''
      }

      if (has('notes') || has('comments')) {
        const notes = clientData.notes ?? clientData.comments ?? ''
        payload.notes = String(notes).trim()
        payload.company_notes = payload.notes
      }

      if (clientData.preferences) payload.preferences = clientData.preferences

      const response = await api.put(`/clients/${clientId}`, payload, true, tenantId ? { tenantId } : {})
      return response
    } catch (error) {
      console.error('Error actualizando cliente:', error)
      throw error
    }
  },

  async deactivate(clientId) {
    try {
      const response = await api.patch(`/clients/${clientId}/deactivate`, {}, true)
      return response
    } catch (error) {
      console.error('Error pausando cliente:', error)
      throw error
    }
  },

  async reactivate(clientId) {
    try {
      const response = await api.patch(`/clients/${clientId}/reactivate`, {}, true)
      return response
    } catch (error) {
      console.error('Error reactivando cliente:', error)
      throw error
    }
  },

  async getClientRecordNotes(clientId, filters = {}) {
    try {
      const params = new URLSearchParams()
      if (filters.q) params.set('q', filters.q)
      if (filters.category) params.set('category', filters.category)
      if (filters.tag) params.set('tag', filters.tag)
      if (filters.author_id) params.set('author_id', String(filters.author_id))
      if (filters.is_pinned !== undefined && filters.is_pinned !== '') params.set('is_pinned', String(filters.is_pinned))
      const qs = params.toString()
      return await api.get(`/clients/${clientId}/record/notes${qs ? `?${qs}` : ''}`, true)
    } catch (error) {
      console.error('Error obteniendo notas internas del cliente:', error)
      throw error
    }
  },

  async createClientRecordNote(clientId, noteData) {
    try {
      return await api.post(`/clients/${clientId}/record/notes`, noteData, true)
    } catch (error) {
      console.error('Error creando nota interna del cliente:', error)
      throw error
    }
  },

  async updateClientRecordNote(clientId, noteId, noteData) {
    try {
      return await api.put(`/clients/${clientId}/record/notes/${noteId}`, noteData, true)
    } catch (error) {
      console.error('Error actualizando nota interna del cliente:', error)
      throw error
    }
  },

  async deleteClientRecordNote(clientId, noteId) {
    try {
      return await api.delete(`/clients/${clientId}/record/notes/${noteId}`, true)
    } catch (error) {
      console.error('Error eliminando nota interna del cliente:', error)
      throw error
    }
  },

  async getClientRecordNoteHistory(clientId, noteId) {
    try {
      return await api.get(`/clients/${clientId}/record/notes/${noteId}/history`, true)
    } catch (error) {
      console.error('Error obteniendo historial de nota interna:', error)
      throw error
    }
  },
  /*Elimina un cliente*/
  async delete(clientId) {
    try {
      const response = await api.delete(`/clients/${clientId}`, true)
      return response
    } catch (error) {
      console.error('Error eliminando cliente:', error)
      throw error
    }
  },

  /**
   * Busca clientes por texto (nombre, teléfono, etc.).
   * Alcance (solo mis clientes vs todos en la empresa) lo aplica el backend según JWT, tenant y rol.
   */
}
